import { useCallback, useEffect, useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";
import { Info, RotateCcw } from "react-feather";
import { message, Tooltip } from "antd";
import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import { BRAND_ICONS_URLS } from "../../../utils/Constants";
import { SPORTSBOOK_TIERS } from "../../../utils/constants/sportsbookTiers";
import { useSelector } from "react-redux";
import freeCashIcon from "../../../assets/images/free-cash.png";
import "./stylesBetLimits.css";

const TIERS = [
  SPORTSBOOK_TIERS.DEFAULT,
  SPORTSBOOK_TIERS.VIP,
  SPORTSBOOK_TIERS.AFFILIATE,
];
const TIER_TITLES = {
  [SPORTSBOOK_TIERS.DEFAULT]: "Default user limits",
  [SPORTSBOOK_TIERS.VIP]: "VIP user limits",
  [SPORTSBOOK_TIERS.AFFILIATE]: "Affiliate user limits",
};
const QUICK_BET_DEFAULT_COUNT = 3;
const DEFAULT_QUICK_BET_DEFAULTS = [0, 0, 0];

/** Real money (no secondary in config): primary + free cash — everyone supports free cash. */
export const BET_LIMIT_CURRENCY_IDS_CASH_FREE = ["rebet_cash", "rebet_free_cash"];

/** Sweeps-style clients with coin: primary + secondary + free cash (e.g. ReBet). */
export const BET_LIMIT_CURRENCY_IDS_SWEEPSTAKES = [
  "rebet_cash",
  "rebet_coin",
  "rebet_free_cash",
];

/**
 * Currency config matching API keys (rebet_cash, rebet_coin, rebet_free_cash).
 */
const DEFAULT_CURRENCIES = [
  { id: "rebet_cash", label: "ReBet Cash", symbol: "$" },
  { id: "rebet_coin", label: "ReBet Coin", symbol: "" },
  { id: "rebet_free_cash", label: AppStrings.FREE_CASH, symbol: "" },
];

function hasSecondaryCurrencyInConfig(appCurrencyConfig) {
  const s = appCurrencyConfig?.secondary_currency;
  return s != null && typeof s === "object";
}

/**
 * GET/PUT keys: triple when `secondary_currency` exists (sweeps + coin); else primary + free cash (real money).
 * When config is not hydrated (`null`), assume triple so the first GET can parse full payloads.
 */
export function getBetLimitCurrencyIds(appCurrencyConfig) {
  if (appCurrencyConfig == null) return BET_LIMIT_CURRENCY_IDS_SWEEPSTAKES;
  return hasSecondaryCurrencyInConfig(appCurrencyConfig)
    ? BET_LIMIT_CURRENCY_IDS_SWEEPSTAKES
    : BET_LIMIT_CURRENCY_IDS_CASH_FREE;
}

/**
 * UI cards: real money → primary + free cash; sweeps with coin → primary + coin + free cash.
 */
export function buildBetLimitCurrencies(appCurrencyConfig) {
  if (appCurrencyConfig == null) {
    return DEFAULT_CURRENCIES.map(({ id, label, symbol }) => ({ id, label, symbol }));
  }

  const primary = appCurrencyConfig.primary_currency;
  const secondary = appCurrencyConfig.secondary_currency;
  const defaultSym = appCurrencyConfig.default_currency_symbol ?? "$";
  const primaryLabel =
    (primary?.name && String(primary.name).trim()) || DEFAULT_CURRENCIES[0].label;
  const cashRow = {
    id: "rebet_cash",
    label: primaryLabel,
    symbol: defaultSym,
  };
  const freeCashRow = {
    id: "rebet_free_cash",
    label: AppStrings.FREE_CASH,
    symbol: "",
  };

  if (!hasSecondaryCurrencyInConfig(appCurrencyConfig)) {
    return [cashRow, freeCashRow];
  }

  const secondaryLabel =
    (secondary?.name && String(secondary.name).trim()) || DEFAULT_CURRENCIES[1].label;
  return [
    cashRow,
    {
      id: "rebet_coin",
      label: secondaryLabel,
      symbol: "",
    },
    freeCashRow,
  ];
}

/** Default per-currency limit values. `maxWin` of 0 means "no maximum win cap". */
const EMPTY_LIMIT = { min: 0, max: 100000, maxWin: 0 };

/**
 * Build empty tier limits structure for the given API currency keys.
 */
function emptyTierLimits(currencyIds) {
  return TIERS.reduce((acc, tier) => {
    acc[tier] = currencyIds.reduce((cur, cid) => {
      cur[cid] = { ...EMPTY_LIMIT };
      return cur;
    }, {});
    return acc;
  }, {});
}

/**
 * Parse GET response: min/max/maxWin objects include rebet_cash, rebet_coin, rebet_free_cash per tier when present.
 */
function parseBetLimitsResponse(data, currencyIds) {
  const out = emptyTierLimits(currencyIds);
  if (!data || typeof data !== "object") return out;
  TIERS.forEach((tier) => {
    const tierData = data[tier];
    if (!tierData) return;
    const minObj = tierData.min_bet_placement_amount;
    const maxObj = tierData.max_bet_placement_amount;
    const maxWinObj = tierData.max_win_amount;
    currencyIds.forEach((cid) => {
      const min = Number(minObj?.[cid] ?? 0) || 0;
      const max = Number(maxObj?.[cid] ?? 100000) || 100000;
      const maxWin = Number(maxWinObj?.[cid] ?? 0) || 0;
      out[tier][cid] = { min, max, maxWin };
    });
  });
  return out;
}

/**
 * Build a partial PUT body for a single currency card so each card saves independently.
 *
 * The bet-limits update endpoint requires all tier keys (`default`, `vip`, and `affiliate`)
 * but treats an empty tier object as "leave this tier untouched": its server-side merge seeds
 * from the stored record and only overlays the money-type keys actually provided. So we send
 * the edited tier/currency and `{}` for the other tiers — omitting a tier key entirely would
 * 422, and an empty `{}` preserves (never clears) the untouched tier's stored values.
 *
 * `max_win_amount` is only included when `includeMaxWin` is true (gated by the
 * `sportsbook.max_win_amount` feature flag).
 */
function buildCardPayload(tierLimits, tier, currencyId, includeMaxWin = false) {
  const { min, max, maxWin } = tierLimits[tier]?.[currencyId] || EMPTY_LIMIT;
  const tierPayload = {
    min_bet_placement_amount: { [currencyId]: min },
    max_bet_placement_amount: { [currencyId]: max },
  };
  if (includeMaxWin) {
    tierPayload.max_win_amount = { [currencyId]: maxWin };
  }
  return TIERS.reduce((payload, currentTier) => {
    payload[currentTier] = currentTier === tier ? tierPayload : {};
    return payload;
  }, {});
}

function toFiniteAmount(value) {
  const num = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(num) ? num : 0;
}

function normalizeQuickBetDefaults(values) {
  const source = Array.isArray(values) ? values : [];
  return Array.from({ length: QUICK_BET_DEFAULT_COUNT }, (_, index) =>
    toFiniteAmount(source[index])
  );
}

export function parseQuickBetDefaultsResponse(data) {
  const payload = data?.data ?? data;
  if (!Array.isArray(payload?.amounts)) {
    throw new Error("[QuickBetDefaults] unexpected response shape");
  }
  return normalizeQuickBetDefaults(payload.amounts);
}

export function buildQuickBetDefaultsPayload(defaults) {
  return {
    amounts: normalizeQuickBetDefaults(defaults),
  };
}

export function getQuickBetDefaultsValidationError(defaults) {
  const amounts = normalizeQuickBetDefaults(defaults);
  if (amounts.some((amount) => amount <= 0)) {
    return AppStrings.QUICK_BET_DEFAULTS_POSITIVE_ERROR;
  }
  if (amounts.some((amount, index) => index > 0 && amount <= amounts[index - 1])) {
    return AppStrings.QUICK_BET_DEFAULTS_ASCENDING_ERROR;
  }
  return "";
}

function BetLimits({ children = undefined }) {
  const { appConfig } = useSelector((state) => state.config);
  const quickBetDefaultsEnabled = Boolean(
    appConfig?.features_status_config?.sportsbook?.quick_bet_defaults
  );
  // Feature flag for the per-currency "Maximum Win Amount" cap (SPENG-2295).
  // Off by default. IMPORTANT: the backend bet-limits endpoint
  // (admin-services GET/PUT /admin-panel/settings/v1/bet-limits) does not yet
  // support `max_win_amount` — it is silently dropped on PUT and never returned
  // on GET. Keep this flag OFF until that backend support ships, otherwise the
  // field will appear editable but will not persist across reloads.
  // SPENG-2295: per-currency "Maximum Win Amount" cap, gated by the
  // sportsbook.max_win_amount feature flag (off by default).
  const maxWinEnabled = Boolean(
    appConfig?.features_status_config?.sportsbook?.max_win_amount
  );
  const token = JSON.parse(localStorage.getItem("token"));
  const accessToken = token?.AccessToken;
  const PrivateAdminPanelInstance = useMemo(
    () => createPrivateAdminPanelInstance(accessToken),
    [accessToken]
  );

  const betLimitCurrencyIds = useMemo(
    () => getBetLimitCurrencyIds(appConfig?.app_currency_config),
    [appConfig?.app_currency_config]
  );

  const currencies = useMemo(() => {
    const base = process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL || "";
    return buildBetLimitCurrencies(appConfig?.app_currency_config).map((row) => {
      let iconSrc;
      if (row.id === "rebet_cash") {
        iconSrc = base + BRAND_ICONS_URLS.primaryCurrencySymbol;
      } else if (row.id === "rebet_coin") {
        iconSrc = base + BRAND_ICONS_URLS.secondaryCurrencySymbol;
      } else if (row.id === "rebet_free_cash") {
        iconSrc = freeCashIcon;
      } else {
        iconSrc = base + BRAND_ICONS_URLS.primaryCurrencySymbol;
      }
      return { ...row, iconSrc };
    });
  }, [appConfig?.app_currency_config]);
  const [tierLimits, setTierLimits] = useState(() =>
    emptyTierLimits(BET_LIMIT_CURRENCY_IDS_SWEEPSTAKES)
  );
  const [initialTierLimits, setInitialTierLimits] = useState(() =>
    emptyTierLimits(BET_LIMIT_CURRENCY_IDS_SWEEPSTAKES)
  );
  const [loading, setLoading] = useState(true);
  /** idle = form may render; missing_config | error = show message instead of fake defaults */
  const [loadIssue, setLoadIssue] = useState(null);
  const [loadIssueMessage, setLoadIssueMessage] = useState("");
  /** Only the clicked card shows the save loader; all Save buttons disabled while non-null. */
  const [savingKey, setSavingKey] = useState(null);
  const [quickBetDefaults, setQuickBetDefaults] = useState(DEFAULT_QUICK_BET_DEFAULTS);
  const [initialQuickBetDefaults, setInitialQuickBetDefaults] = useState(
    DEFAULT_QUICK_BET_DEFAULTS
  );
  const [quickBetLoading, setQuickBetLoading] = useState(true);
  const [quickBetLoadIssue, setQuickBetLoadIssue] = useState(false);
  const [quickBetLoadIssueMessage, setQuickBetLoadIssueMessage] = useState("");
  const [quickBetSaving, setQuickBetSaving] = useState(false);

  const fetchBetLimits = useCallback(async () => {
    setLoading(true);
    setLoadIssue(null);
    setLoadIssueMessage("");
    const currencyIds = getBetLimitCurrencyIds(appConfig?.app_currency_config);
    try {
      const response = await PrivateAdminPanelInstance.get(API_ROUTES.GET_SPORTSBOOK_BET_LIMITS);
      const raw = response?.data;
      const data = raw?.data ?? raw;
      const parsed = parseBetLimitsResponse(data, currencyIds);
      setTierLimits(parsed);
      setInitialTierLimits(parsed);
    } catch (err) {
      console.error("[BetLimits] fetch error", err);
      const apiMsg = getApiErrorMessage(
        err,
        AppStrings.FAILED_TO_LOAD_BET_LIMITS
      );
      message.error(apiMsg);
      const code = err?.response?.data?.error_code;
      setLoadIssueMessage(apiMsg);
      setLoadIssue(code === "BET_LIMITS_NOT_FOUND" ? "missing_config" : "error");
      setTierLimits(emptyTierLimits(currencyIds));
      setInitialTierLimits(emptyTierLimits(currencyIds));
    } finally {
      setLoading(false);
    }
  }, [PrivateAdminPanelInstance, appConfig?.app_currency_config]);

  useEffect(() => {
    fetchBetLimits();
  }, [fetchBetLimits]);

  const fetchQuickBetDefaults = useCallback(async () => {
    setQuickBetLoading(true);
    setQuickBetLoadIssue(false);
    setQuickBetLoadIssueMessage("");
    try {
      const response = await PrivateAdminPanelInstance.get(
        API_ROUTES.GET_QUICK_BET_DEFAULTS
      );
      const parsed = parseQuickBetDefaultsResponse(response?.data);
      setQuickBetDefaults(parsed);
      setInitialQuickBetDefaults(parsed);
    } catch (err) {
      console.error("[BetLimits] quick bet defaults fetch error", err);
      const apiMsg = getApiErrorMessage(
        err,
        AppStrings.FAILED_TO_LOAD_QUICK_BET_DEFAULTS
      );
      message.error(apiMsg);
      setQuickBetLoadIssue(true);
      setQuickBetLoadIssueMessage(apiMsg);
      setQuickBetDefaults(DEFAULT_QUICK_BET_DEFAULTS);
      setInitialQuickBetDefaults(DEFAULT_QUICK_BET_DEFAULTS);
    } finally {
      setQuickBetLoading(false);
    }
  }, [PrivateAdminPanelInstance]);

  useEffect(() => {
    if (!quickBetDefaultsEnabled) {
      return;
    }
    fetchQuickBetDefaults();
  }, [fetchQuickBetDefaults, quickBetDefaultsEnabled]);

  const quickBetCurrencyCode =
    appConfig?.app_currency_config?.primary_currency?.shortcode ||
    appConfig?.app_currency_config?.default_currency ||
    "";
  const quickBetCashIconSrc =
    currencies.find((currency) => currency.id === "rebet_cash")?.iconSrc || "";

  const updateLimit = (tier, currencyId, field, value) => {
    const num = typeof value === "number" ? value : parseFloat(value);
    setTierLimits((prev) => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        [currencyId]: {
          ...(prev[tier]?.[currencyId] || EMPTY_LIMIT),
          [field]: isNaN(num) ? 0 : num,
        },
      },
    }));
  };

  const isDirty = (tier, currencyId) => {
    const init = initialTierLimits[tier]?.[currencyId] || EMPTY_LIMIT;
    const cur = tierLimits[tier]?.[currencyId] || EMPTY_LIMIT;
    const baseDirty = cur.min !== init.min || cur.max !== init.max;
    return maxWinEnabled ? baseDirty || cur.maxWin !== init.maxWin : baseDirty;
  };

  const handleReset = (tier, currencyId) => {
    const init = initialTierLimits[tier]?.[currencyId] || EMPTY_LIMIT;
    setTierLimits((prev) => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        [currencyId]: { ...init },
      },
    }));
  };

  /** Persist a single currency card; the endpoint merges partial payloads, so only this
   *  card's baseline is reset and other edited cards stay dirty/saveable. */
  const handleSaveCard = async (tier, currencyId) => {
    const key = `${tier}-${currencyId}`;
    setSavingKey(key);
    try {
      const payload = buildCardPayload(tierLimits, tier, currencyId, maxWinEnabled);
      await PrivateAdminPanelInstance.put(API_ROUTES.UPDATE_SPORTSBOOK_BET_LIMITS, payload);
      const savedCard = { ...(tierLimits[tier]?.[currencyId] || EMPTY_LIMIT) };
      setInitialTierLimits((prev) => ({
        ...prev,
        [tier]: { ...prev[tier], [currencyId]: savedCard },
      }));
      message.success(AppStrings.SAVE_BET_LIMITS_SUCCESS);
    } catch (err) {
      console.error("[BetLimits] save error", err);
      message.error(
        getApiErrorMessage(err, AppStrings.FAILED_TO_SAVE_BET_LIMITS)
      );
    } finally {
      setSavingKey(null);
    }
  };

  const updateQuickBetDefault = (index, value) => {
    const amount = toFiniteAmount(value);
    setQuickBetDefaults((prev) =>
      prev.map((current, currentIndex) =>
        currentIndex === index ? amount : current
      )
    );
  };

  const quickBetDefaultsDirty = quickBetDefaults.some(
    (amount, index) => amount !== initialQuickBetDefaults[index]
  );

  const handleResetQuickBetDefaults = () => {
    setQuickBetDefaults([...initialQuickBetDefaults]);
  };

  const handleSaveQuickBetDefaults = async () => {
    const validationError = getQuickBetDefaultsValidationError(quickBetDefaults);
    if (validationError) {
      message.error(validationError);
      return;
    }

    setQuickBetSaving(true);
    try {
      const payload = buildQuickBetDefaultsPayload(quickBetDefaults);
      const response = await PrivateAdminPanelInstance.put(
        API_ROUTES.UPDATE_QUICK_BET_DEFAULTS,
        payload
      );
      let confirmedDefaults = payload.amounts;
      if (response?.data != null) {
        try {
          confirmedDefaults = parseQuickBetDefaultsResponse(response.data);
        } catch {
          // PUT already succeeded; keep the UI in sync with the saved request
          // when the backend returns a success envelope without amounts.
        }
      }
      setQuickBetDefaults(confirmedDefaults);
      setInitialQuickBetDefaults(confirmedDefaults);
      message.success(AppStrings.SAVE_QUICK_BET_DEFAULTS_SUCCESS);
    } catch (err) {
      console.error("[BetLimits] quick bet defaults save error", err);
      message.error(
        getApiErrorMessage(err, AppStrings.FAILED_TO_SAVE_QUICK_BET_DEFAULTS)
      );
    } finally {
      setQuickBetSaving(false);
    }
  };

  const renderQuickBetDefaults = () =>
    quickBetDefaultsEnabled ? (
      <div className="sportsbook-bet-limits-quick-bet-card">
      <h2 className="sportsbook-bet-limits-quick-bet-section-title">
        {AppStrings.BET_SLIP}
      </h2>
      <div className="sportsbook-bet-limits-quick-bet-header">
        <h3>{AppStrings.QUICK_BET_DEFAULTS}</h3>
        {quickBetCashIconSrc ? (
          <img
            src={quickBetCashIconSrc}
            alt=""
            className="sportsbook-bet-limits-quick-bet-icon"
          />
        ) : null}
      </div>
      <p className="sportsbook-bet-limits-quick-bet-description">
        {AppStrings.QUICK_BET_DEFAULTS_DESCRIPTION}
      </p>
      {quickBetLoading ? (
        <div className="sportsbook-bet-limits-quick-bet-loading">
          <RebetLoader height="48px" width="48px" />
        </div>
      ) : null}
      {!quickBetLoading && quickBetLoadIssue ? (
        <div className="sportsbook-bet-limits-load-issue">
          <div className="sportsbook-bet-limits-error" role="alert">
            {quickBetLoadIssueMessage}
          </div>
          <button
            type="button"
            className="sportsbook-bet-limits-retry"
            onClick={() => fetchQuickBetDefaults()}
          >
            {AppStrings.BET_LIMITS_TRY_AGAIN}
          </button>
        </div>
      ) : null}
      {!quickBetLoading && !quickBetLoadIssue ? (
        <>
          <div className="sportsbook-bet-limits-quick-bet-fields">
            {quickBetDefaults.map((amount, index) => (
              <div
                key={`quick-bet-default-${index}`}
                className="sportsbook-bet-limits-field"
              >
                <label htmlFor={`quick-bet-default-${index}`}>
                  {AppStrings.QUICK_BET_DEFAULT_LABEL.replace(
                    "{index}",
                    String(index + 1)
                  )}
                </label>
                <NumericFormat
                  id={`quick-bet-default-${index}`}
                  value={amount}
                  onValueChange={({ floatValue }) =>
                    updateQuickBetDefault(index, floatValue ?? 0)
                  }
                  thousandSeparator
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  suffix={quickBetCurrencyCode ? ` ${quickBetCurrencyCode}` : ""}
                  className="sportsbook-bet-limits-input"
                />
              </div>
            ))}
          </div>
          <div className="sportsbook-bet-limits-actions">
            <button
              type="button"
              className="sportsbook-bet-limits-reset"
              onClick={handleResetQuickBetDefaults}
              disabled={!quickBetDefaultsDirty || quickBetSaving}
            >
              <RotateCcw size={16} />
              {AppStrings.RESET}
            </button>
            <Tooltip title={AppStrings.SAVE_QUICK_BET_DEFAULTS_TOOLTIP}>
              <span
                style={
                  !quickBetDefaultsDirty || quickBetSaving
                    ? { cursor: "not-allowed" }
                    : undefined
                }
              >
                <button
                  type="button"
                  className={`sportsbook-bet-limits-save ${
                    quickBetDefaultsDirty ? "active" : ""
                  }`}
                  disabled={!quickBetDefaultsDirty || quickBetSaving}
                  onClick={handleSaveQuickBetDefaults}
                  style={
                    !quickBetDefaultsDirty || quickBetSaving
                      ? { pointerEvents: "none" }
                      : undefined
                  }
                >
                  {quickBetSaving ? (
                    <RebetLoader height="20px" width="20px" />
                  ) : (
                    AppStrings.SAVE_CHANGES_BUTTON || AppStrings.SAVE_CHANGES
                  )}
                </button>
              </span>
            </Tooltip>
          </div>
        </>
      ) : null}
      </div>
    ) : null;

  if (loading) {
    return (
      <div className="sportsbook-bet-limits">
        {children}
        <div className="sportsbook-bet-limits-loading">
          <RebetLoader height="80px" width="80px" />
        </div>
      </div>
    );
  }

  return (
    <div className="sportsbook-bet-limits">
      {children}
      <h2 className="sportsbook-bet-limits-title">{AppStrings.SET_LIMITS}</h2>
      {loadIssue ? (
        <div className="sportsbook-bet-limits-load-issue">
          <div className="sportsbook-bet-limits-error" role="alert">
            {loadIssueMessage}
          </div>
          <button
            type="button"
            className="sportsbook-bet-limits-retry"
            onClick={() => fetchBetLimits()}
          >
            {AppStrings.BET_LIMITS_TRY_AGAIN}
          </button>
        </div>
      ) : null}
      {!loadIssue ? (
        <div className="sportsbook-bet-limits-tier-sections">
          {TIERS.map((tier) => (
            <div key={tier} className="sportsbook-bet-limits-tier-section">
              <h3 className="sportsbook-bet-limits-tier-title">
                {TIER_TITLES[tier]}
              </h3>
              <div className="sportsbook-bet-limits-currency-list">
                {currencies.map((currency) => {
                  const cur = tierLimits[tier]?.[currency.id] || EMPTY_LIMIT;
                  const dirty = isDirty(tier, currency.id);
                  const cardKey = `${tier}-${currency.id}`;
                  const isSavingThisCard = savingKey === cardKey;
                  const saveInFlight = savingKey != null;
                  return (
                    <div key={cardKey} className="sportsbook-bet-limits-currency-card">
                      <div className="sportsbook-bet-limits-currency-header">
                        <span className="sportsbook-bet-limits-currency-label">{currency.label}</span>
                        <img
                          src={currency.iconSrc}
                          alt=""
                          className="sportsbook-bet-limits-currency-icon-img"
                        />
                      </div>
                      <div className="sportsbook-bet-limits-fields">
                        <div className="sportsbook-bet-limits-field">
                          <label>
                            {AppStrings.MINIMUM_BET_AMOUNT}
                            <Info size={14} className="sportsbook-bet-limits-info-icon" />
                          </label>
                          <NumericFormat
                            value={cur.min}
                            onValueChange={({ floatValue }) =>
                              updateLimit(tier, currency.id, "min", floatValue ?? 0)
                            }
                            thousandSeparator
                            decimalScale={2}
                            allowNegative={false}
                            aria-label={`${AppStrings.MINIMUM_BET_AMOUNT} ${currency.label} ${tier}`}
                            className="sportsbook-bet-limits-input"
                          />
                        </div>
                        <div className="sportsbook-bet-limits-field">
                          <label>
                            {AppStrings.MAXIMUM_BET_AMOUNT}
                            <Info size={14} className="sportsbook-bet-limits-info-icon" />
                          </label>
                          <NumericFormat
                            value={cur.max}
                            onValueChange={({ floatValue }) =>
                              updateLimit(tier, currency.id, "max", floatValue ?? 0)
                            }
                            thousandSeparator
                            decimalScale={2}
                            allowNegative={false}
                            aria-label={`${AppStrings.MAXIMUM_BET_AMOUNT} ${currency.label} ${tier}`}
                            className="sportsbook-bet-limits-input"
                          />
                        </div>
                        {maxWinEnabled ? (
                          <div className="sportsbook-bet-limits-field">
                            <label>
                              {AppStrings.MAXIMUM_WIN_AMOUNT}
                              <Info size={14} className="sportsbook-bet-limits-info-icon" />
                            </label>
                            <NumericFormat
                              value={cur.maxWin}
                              onValueChange={({ floatValue }) =>
                                updateLimit(tier, currency.id, "maxWin", floatValue ?? 0)
                              }
                              thousandSeparator
                              decimalScale={2}
                              allowNegative={false}
                              placeholder={AppStrings.MAXIMUM_WIN_AMOUNT_HINT}
                              aria-label={`${AppStrings.MAXIMUM_WIN_AMOUNT} ${currency.label} ${tier}`}
                              className="sportsbook-bet-limits-input"
                            />
                            <span className="sportsbook-bet-limits-field-hint">
                              {AppStrings.MAXIMUM_WIN_AMOUNT_HINT}
                            </span>
                          </div>
                        ) : null}
                      </div>
                      <div className="sportsbook-bet-limits-actions">
                        <button
                          type="button"
                          className="sportsbook-bet-limits-reset"
                          onClick={() => handleReset(tier, currency.id)}
                          disabled={!dirty}
                        >
                          <RotateCcw size={16} />
                          {AppStrings.RESET}
                        </button>
                        <Tooltip title={AppStrings.SAVE_CURRENCY_TOOLTIP}>
                          <span style={!dirty || saveInFlight ? { cursor: "not-allowed" } : undefined}>
                            <button
                              type="button"
                              className={`sportsbook-bet-limits-save ${dirty ? "active" : ""}`}
                              disabled={!dirty || saveInFlight}
                              onClick={() => handleSaveCard(tier, currency.id)}
                              style={!dirty || saveInFlight ? { pointerEvents: "none" } : undefined}
                            >
                              {isSavingThisCard ? (
                                <RebetLoader height="20px" width="20px" />
                              ) : (
                                AppStrings.SAVE_CHANGES_BUTTON || AppStrings.SAVE_CHANGES
                              )}
                            </button>
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {renderQuickBetDefaults()}
    </div>
  );
}

export default BetLimits;
